import React, { useEffect, useRef } from 'react';
import { contactme, socialIcons } from './data';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './contact.scss';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
    const contactRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef([]);
    const socialRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                {
                    y: 50,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: 'power3.out'
                }
            );

            // Cards animation
            const cards = document.querySelectorAll('.contact-card');
            gsap.fromTo(cards,
                {
                    y: 80,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: contactRef.current,
                        start: 'top 70%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.1,
                    ease: 'power3.out'
                }
            );

            // Social icons animation
            const socialIcons = document.querySelectorAll('.social-link');
            gsap.fromTo(socialIcons,
                {
                    scale: 0,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: '.social-links',
                        start: 'top 80%',
                    },
                    scale: 1,
                    opacity: 1,
                    duration: 0.5,
                    stagger: 0.1,
                    ease: 'back.out(1.7)'
                }
            );

        }, contactRef);

        return () => ctx.revert();
    }, []);

    return (
        <section className="contact" ref={contactRef}>
            <div className="contact-container">
                <h2 className="section-title" ref={titleRef}>
                    Get In Touch
                </h2>
                <p className="section-subtitle">
                    Let's build something amazing together
                </p>

                <div className="contact-grid">
                    {contactme.map((item, index) => (
                        <div
                            key={item.id}
                            className="contact-card"
                            ref={el => cardsRef.current[index] = el}
                        >
                            <div className="contact-icon">
                                <img 
                                    src={require(`../../assets/contact/${item.src}`)} 
                                    alt={item.name}
                                />
                            </div>
                            <div className="contact-info">
                                <h3 className="contact-name">{item.name}</h3>
                                {item.name === 'Whatsapp' ? (
                                    <a 
                                        href={`https://wa.me/${item.details.replace(/[^0-9]/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="contact-details"
                                    >
                                        {item.details}
                                    </a>
                                ) : item.name === 'Email' ? (
                                    <a 
                                        href={`mailto:${item.details}`}
                                        className="contact-details"
                                    >
                                        {item.details}
                                    </a>
                                ) : (
                                    <p className="contact-details">{item.details}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="social-section">
                    <h3 className="social-title">Follow Me</h3>
                    <div className="social-links">
                        {socialIcons.map((social, index) => (
                            <a
                                key={social.id}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-link"
                                ref={el => socialRef.current[index] = el}
                                aria-label={social.name}
                            >
                                <img 
                                    src={require(`../../assets/contact/${social.src}`)} 
                                    alt={social.name}
                                />
                            </a>
                        ))}
                    </div>
                </div>

                <div className="cta-box">
                    <div className="cta-content">
                        <h3>Ready to start a project?</h3>
                        <p>I'm always open to discussing product design work or partnership opportunities.</p>
                        <div className="cta-buttons">
                            <a href="mailto:anishlp7@gmail.com" className="btn-primary">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Send Email
                            </a>
                            <a 
                                href="https://wa.me/919043571929?text=Hi,%20I'd%20like%20to%20discuss%20a%20project"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-secondary"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="currentColor"/>
                                    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.546 20.2c-.18.54.24.96.78.78l3.032-.892A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                WhatsApp Me
                            </a>
                        </div>
                    </div>
                </div>

                <footer className="footer">
                    <p>&copy; {new Date().getFullYear()} Anish Lawrence. All rights reserved.</p>
                    <p className="footer-quote">Crafted with passion and dedication</p>
                </footer>
            </div>
        </section>
    );
};

export default Contact;
